import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from "../context/AuthContext";
import { userApi } from "../api/services";
import toast, { Toaster } from 'react-hot-toast';

// ── Icons ─────────────────────────────────────────────────────────────────────
const iconGuard = (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M6.33279 0.848506C6.43909 0.800498 6.5609 0.800498 6.6672 0.848506C8.68698 1.76067 9.59366 2.0521 11.8301 2.45328C12.0143 2.48631 12.1521 2.64084 12.1638 2.82756C12.5932 9.63258 7.07067 11.9894 6.65176 12.1581C6.55438 12.1973 6.44561 12.1973 6.34823 12.1581C5.92932 11.9894 0.406799 9.63258 0.836154 2.82756C0.847935 2.64084 0.985716 2.48631 1.16987 2.45328C3.40633 2.0521 4.31301 1.76067 6.33279 0.848506Z" fill="#B45329"/>
  </svg>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialFormState = { name: '', email: '', role: 'Viewer' };
  const [formData, setFormData] = useState(initialFormState);

  const { activeTenant, user } = useAuth();
  const resolvedTenant = (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ?? user?.tenant_id;
  const isSuperAdmin   = (user?.role || '').toLowerCase() === 'super_admin';
  // Super admin sees all users for the tenant; regular users are scoped to their own org
  const resolvedOrgId  = isSuperAdmin ? null : (user?.org_id ?? null);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    if (!resolvedTenant) return;
    try {
      setIsLoading(true);
      const response = await userApi.getAll({ tenantId: resolvedTenant, orgId: resolvedOrgId });
      setUsers(response.data?.data || response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load users.");
    } finally {
      setIsLoading(false);
    }
  }, [resolvedTenant, resolvedOrgId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSaveUser = async (e) => {
    e.preventDefault();
    // Bug 2: validate name before submitting
    if (!formData.name.trim()) {
      setFormError('Full name is required.');
      return;
    }
    // Bug 4: prevent double-click / rapid re-submission
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError('');
    try {
      if (isEditing) {
        const res = await userApi.update(currentUserId, {
          full_name: formData.name,
          role: formData.role.toLowerCase(),
        });
        const updated = res.data?.data || res.data;
        setUsers(users.map(u => u.id === currentUserId ? updated : u));
        toast.success("User updated successfully!");
      } else {
        const res = await userApi.create({
          full_name: formData.name,
          email: formData.email,
          role: formData.role.toLowerCase(),
          parent_id: user?.id,
        });
        const created = res.data?.data || res.data;
        setUsers([...users, created]);
        // Bug 3: use the server's message so email failures are surfaced
        toast.success(res.data?.message || "User created! An invitation email has been sent.");
      }
      setIsModalOpen(false);
    } catch (error) {
      setFormError(error.response?.data?.message || error.response?.data?.error || "Failed to save user.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await userApi.delete(userToDelete);
      setUsers(users.filter(u => u.id !== userToDelete));
      toast.success("User deleted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete user.");
    } finally {
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  // ── Resend invitation ───────────────────────────────────────────────────────
  const handleResendInvitation = async (userId, email) => {
    setBusyId(userId);
    try {
      await userApi.resendInvitation(userId);
      toast.success(`Invitation resent to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend invitation.");
    } finally {
      setBusyId(null);
    }
  };

  // ── Modal helpers ───────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setIsEditing(false);
    setFormData(initialFormState);
    setFormError('');
    setIsModalOpen(true);
  };

  const openEditModal = (targetUser) => {
    setIsEditing(true);
    setCurrentUserId(targetUser.id);
    setFormError('');
    setFormData({
      // FIX: was user.org_name || user.name — backend field is full_name
      name: targetUser.full_name || '',
      email: targetUser.email || '',
      role: targetUser.role ? targetUser.role.charAt(0).toUpperCase() + targetUser.role.slice(1) : 'Viewer',
    });
    setIsModalOpen(true);
  };

  const confirmDeleteUser = (id) => {
    setUserToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const filteredUsers = users.filter(u => {
    // Non-super-admins must not see super_admin accounts
    if (!isSuperAdmin && (u.role || '').toLowerCase() === 'super_admin') return false
    const q = searchQuery.toLowerCase()
    return (u.full_name || '').toLowerCase().includes(q) ||
           (u.email    || '').toLowerCase().includes(q)
  });

  const getRoleBadgeStyle = (role) => {
    switch ((role || '').toLowerCase()) {
      case 'admin':
      case 'tenant_admin': return 'bg-blue-100 text-blue-600 border border-blue-200';
      case 'super_admin':  return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
      case 'operator':     return 'bg-orange-100 text-orange-600 border border-orange-200';
      case 'viewer':       return 'bg-slate-500 text-white border border-slate-600';
      default:             return 'bg-gray-100 text-gray-600';
    }
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">Loading users...</div>;
  }

  return (
    <div className="bg-gray-50 min-h-screen p-8 font-sans relative">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your organizational structure and user access</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white px-6 py-2.5 rounded-lg font-medium shadow-sm transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add User
        </button>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Organization Users</h2>
            <p className="text-xs text-gray-500 mt-1">Click on actions to manage individual user permissions.</p>
          </div>
          <div className="relative w-full md:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M19.1573 17.5027L14.7469 13.0922C15.8087 11.6786 16.38 9.958 16.38 8.19C16.38 3.674 12.706 0 8.19 0C3.674 0 0 3.674 0 8.19C0 12.706 3.674 16.38 8.19 16.38C9.958 16.382 11.679 15.809 13.092 14.747L17.503 19.157C17.726 19.357 18.017 19.464 18.316 19.455C18.616 19.447 18.9 19.324 19.112 19.112C19.324 18.9 19.447 18.616 19.455 18.316C19.464 18.017 19.357 17.726 19.157 17.503L19.1573 17.5027ZM2.34 8.19C2.34 5.748 4.228 3.75 6.5 3.75C8.772 3.75 10.66 5.748 10.66 8.19C10.66 10.632 8.772 12.63 6.5 12.63C4.228 12.63 2.34 10.632 2.34 8.19Z" fill="#6B7280"/>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none transition sm:text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.length > 0 ? filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-900 flex items-center justify-center text-white font-bold text-sm">
                        {(u.full_name || u.email || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {u.full_name || <span className="text-gray-400 italic text-xs">Name not set</span>}
                        </div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-md ${getRoleBadgeStyle(u.role)}`}>
                      <div className="flex items-center gap-1.5 capitalize">
                        {iconGuard}
                        {(u.role || '').replace(/_/g, ' ')}
                      </div>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-xs font-semibold ${u.is_active ? 'text-green-600' : 'text-red-500'}`}>
                      {u.is_active ? '● Active' : '● Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'Recently'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-4">
                      {/* NEW: Resend invitation */}
                      <button
                        onClick={() => handleResendInvitation(u.id, u.email)}
                        disabled={busyId === u.id}
                        title="Resend invitation email"
                        className="text-gray-400 hover:text-green-600 transition-colors disabled:opacity-40"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                      </button>
                      {/* Edit */}
                      <button
                        onClick={() => openEditModal(u)}
                        title="Edit User"
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path fillRule="evenodd" clipRule="evenodd" d="M14.5597 2.77858C14.8334 2.77841 15.096 2.88707 15.2895 3.08062L16.4192 4.21031C16.6128 4.40386 16.7214 4.66643 16.7213 4.94015C16.7211 5.21387 16.6121 5.4763 16.4183 5.66962L3.63084 18.4257C3.53502 18.5213 3.42133 18.5971 3.29625 18.6487L1.425 19.4217C1.03983 19.5808 0.596754 19.4925 0.302076 19.1978C0.00739863 18.9031 -0.0809578 18.46 0.0781465 18.0749L0.851115 16.2036C0.902785 16.0785 0.97857 15.9648 1.07415 15.869L13.8302 3.08152C14.0236 2.88773 14.286 2.77875 14.5597 2.77858Z" fill="currentColor"/>
                        </svg>
                      </button>
                      {/* Delete (hide for own account) */}
                      {u.id !== user?.id && (
                        <button
                          onClick={() => confirmDeleteUser(u.id)}
                          title="Delete User"
                          className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <svg width="18" height="21" viewBox="0 0 18 21" fill="none">
                            <path d="M17.25 3H12.75V1.875C12.75 1.37772 12.5525 0.900805 12.2008 0.549175C11.8492 0.197544 11.3723 0 10.875 0H7.125C6.62772 0 6.15081 0.197544 5.79917 0.549175C5.44754 0.900805 5.25 1.37772 5.25 1.875V3H0.75C0.551088 3 0.360322 3.07902 0.21967 3.21967C0.0790178 3.36032 0 3.55109 0 3.75C0 3.94891 0.0790178 4.13968 0.21967 4.28033C0.360322 4.42098 0.551088 4.5 0.75 4.5H1.54688L2.4375 18.7931C2.50406 20.0517 3.46875 21 4.6875 21H13.3125C14.5373 21 15.4828 20.0728 15.5625 18.7969L16.4531 4.5H17.25C17.4489 4.5 17.6397 4.42098 17.7803 4.28033C17.921 4.13968 18 3.94891 18 3.75C18 3.55109 17.921 3.36032 17.7803 3.21967C17.6397 3.07902 17.4489 3 17.25 3ZM11.25 3H6.75V1.875C6.75 1.77554 6.78951 1.68016 6.85984 1.60984C6.93016 1.53951 7.02554 1.5 7.125 1.5H10.875C10.9745 1.5 11.0698 1.53951 11.1402 1.60984C11.2105 1.68016 11.25 1.77554 11.25 1.875V3Z" fill="currentColor"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-gray-400">
                    <p className="text-sm font-medium">No users found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-lg font-bold text-gray-800">{isEditing ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSaveUser} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                  required placeholder="Jane Doe"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                  {isEditing && <span className="text-gray-400 font-normal text-xs ml-2">(cannot be changed)</span>}
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                  required={!isEditing} disabled={isEditing} placeholder="jane@example.com"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:bg-gray-50 disabled:text-gray-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <div className="relative">
                  <select name="role" value={formData.role} onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white transition-all cursor-pointer">
                    <option value="Admin">Admin</option>
                    <option value="Operator">Operator</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              {!isEditing && (
                <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  📧 An invitation email with a password setup link will be sent to this address.
                </p>
              )}
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-800 text-white font-medium rounded-lg hover:bg-blue-900 shadow-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update User' : 'Create & Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ── */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Delete User?</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to delete this user? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteUser}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 shadow-md transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;